import { useRef, useState, useEffect } from 'react'
import { Camera, Trash2 } from 'lucide-react'
import { imageApi, siteApi, type NavLink } from '@/lib/api'
import { useToast } from '@/stores/toast-store'
import { ImageCropDialog } from '@/components/ui/image-crop-dialog'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MB

const DEFAULT_NAV_LINKS: NavLink[] = [
  { label: 'About Me',   href: '' },
  { label: 'Portfolio',  href: '' },
  { label: 'Website',    href: '' },
  { label: 'Reviews',    href: '' },
  { label: 'Contact Me', href: '' },
]

const TABS = ['Background', 'Navigasi'] as const
type Tab = typeof TABS[number]

export default function HomepageSettingsPage() {
  const { toast } = useToast()

  const [activeTab, setActiveTab] = useState<Tab>('Background')

  // ── Background image state ──────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [currentBgUrl, setCurrentBgUrl] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [cropSrcUrl, setCropSrcUrl] = useState<string | null>(null)
  const [webpBlob, setWebpBlob] = useState<Blob | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'uploading' | 'saving'>('idle')

  // ── Nav links state ─────────────────────────────────────────────────────────
  const [navItems, setNavItems] = useState<NavLink[]>(DEFAULT_NAV_LINKS)
  const [isNavSubmitting, setIsNavSubmitting] = useState(false)

  // Fetch settings on mount
  useEffect(() => {
    siteApi.getSettings()
      .then((s) => {
        setCurrentBgUrl(s.bg_image_url)
        setNavItems(s.nav_links?.length ? s.nav_links : DEFAULT_NAV_LINKS)
      })
      .catch(() => {})
  }, [])

  // Revoke preview blob URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Background image handlers ───────────────────────────────────────────────
  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast({ title: 'File tidak valid', description: 'Pilih file gambar (JPG, PNG, GIF, dll)', variant: 'destructive' })
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      toast({ title: 'File terlalu besar', description: 'Ukuran maksimal adalah 5 MB', variant: 'destructive' })
      return
    }

    if (cropSrcUrl) URL.revokeObjectURL(cropSrcUrl)
    setCropSrcUrl(URL.createObjectURL(file))
    e.target.value = ''
  }

  function handleCropConfirm(croppedBlob: Blob) {
    if (cropSrcUrl) URL.revokeObjectURL(cropSrcUrl)
    setCropSrcUrl(null)

    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl)
    setWebpBlob(croppedBlob)
    setPreviewUrl(URL.createObjectURL(croppedBlob))
  }

  function handleCropCancel() {
    if (cropSrcUrl) URL.revokeObjectURL(cropSrcUrl)
    setCropSrcUrl(null)
  }

  async function handleBgSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!webpBlob) return

    setIsSubmitting(true)
    try {
      setSubmitStatus('uploading')
      const { url } = await imageApi.uploadImage(webpBlob)

      setSubmitStatus('saving')
      await siteApi.updateBgImage(url)

      setCurrentBgUrl(url)
      setWebpBlob(null)
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)

      toast({ title: 'Background berhasil diperbarui' })
    } catch (err) {
      toast({
        title: 'Gagal memperbarui background',
        description: (err as Error).message,
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
      setSubmitStatus('idle')
    }
  }

  // ── Nav links handlers ──────────────────────────────────────────────────────
  function updateNavItem(index: number, field: keyof NavLink, value: string) {
    setNavItems((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value } : item))
  }

  function removeNavItem(index: number) {
    setNavItems((prev) => prev.filter((_, i) => i !== index))
  }

  function addNavItem() {
    setNavItems((prev) => [...prev, { label: '', href: '' }])
  }

  async function handleNavSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsNavSubmitting(true)
    try {
      await siteApi.updateNavLinks(navItems)
      toast({ title: 'Navigasi berhasil diperbarui' })
    } catch (err) {
      toast({
        title: 'Gagal memperbarui navigasi',
        description: (err as Error).message,
        variant: 'destructive',
      })
    } finally {
      setIsNavSubmitting(false)
    }
  }

  const displayUrl = previewUrl ?? currentBgUrl

  const submitLabel =
    submitStatus === 'uploading'
      ? 'Mengunggah foto...'
      : submitStatus === 'saving'
        ? 'Menyimpan...'
        : 'Simpan'

  const inputClass = 'rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Homepage</h1>
        <p className="text-sm text-muted-foreground">Atur tampilan halaman utama website.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setActiveTab(t)}
            className={[
              'px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors',
              activeTab === t
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Tab: Background ── */}
      {activeTab === 'Background' && (
        <form onSubmit={handleBgSubmit} className="max-w-lg space-y-6 rounded-xl border border-border bg-card p-6">
          <div className="space-y-3">
            <p className="text-sm font-medium">Background Image</p>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting}
              className="group relative w-full overflow-hidden rounded-lg border border-input bg-muted focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              aria-label="Ganti background homepage"
            >
              {displayUrl ? (
                <img src={displayUrl} alt="Background homepage" className="w-full h-auto block" />
              ) : (
                <div className="flex h-32 w-full items-center justify-center text-sm text-muted-foreground">
                  Belum ada background
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="h-5 w-5 text-white" />
                <span className="text-sm font-medium text-white">Ganti foto</span>
              </div>
            </button>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
                className="text-sm font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
              >
                Pilih foto
              </button>
              <span className="text-xs text-muted-foreground">JPG, PNG, GIF, WebP · Maks. 5 MB</span>
            </div>

            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !webpBlob}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {submitLabel}
          </button>
        </form>
      )}

      {/* ── Tab: Navigasi ── */}
      {activeTab === 'Navigasi' && (
        <form onSubmit={handleNavSubmit} className="max-w-lg space-y-4 rounded-xl border border-border bg-card p-6">
          <div>
            <p className="text-sm font-medium">Navigasi</p>
            <p className="text-xs text-muted-foreground mt-0.5">Link dibuka di tab baru.</p>
          </div>

          <div className="space-y-2">
            {navItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item.label}
                  onChange={(e) => updateNavItem(i, 'label', e.target.value)}
                  placeholder="Label"
                  maxLength={60}
                  disabled={isNavSubmitting}
                  className={`w-28 shrink-0 ${inputClass}`}
                />
                <input
                  type="url"
                  value={item.href}
                  onChange={(e) => updateNavItem(i, 'href', e.target.value)}
                  placeholder="https://..."
                  disabled={isNavSubmitting}
                  className={`flex-1 ${inputClass}`}
                />
                <button
                  type="button"
                  onClick={() => removeNavItem(i)}
                  disabled={isNavSubmitting}
                  aria-label="Hapus item"
                  className="shrink-0 rounded-md p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addNavItem}
            disabled={isNavSubmitting}
            className="text-sm font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
          >
            + Tambah link
          </button>

          <div>
            <button
              type="submit"
              disabled={isNavSubmitting}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              {isNavSubmitting ? 'Menyimpan...' : 'Simpan Navigasi'}
            </button>
          </div>
        </form>
      )}

      {/* Crop dialog */}
      <ImageCropDialog
        imageSrc={cropSrcUrl}
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />
    </div>
  )
}
